<template>
  <div>
    <div class="bg-white pr-4 pl-4 position-sticky top-0 z-index-9999">
      <v-text-field
        density="comfortable"
        rounded
        min-width="400"
        clear-icon="$x"
        :placeholder="$t('general.search')"
        :aria-label="$t('general.search')"
        prepend-inner-icon="$search"
        data-testid="select-hierarchy-search"
        clearable
        variant="filled"
        bg-color="indigo-faint"
        :hide-details="true"
        @update:model-value="workspaceSearch = $event"
      >
      </v-text-field>
    </div>

    <v-list>
      <v-list-item density="comfortable">
        <template v-slot:title>
          <div class="caption">
            <span v-text="$t('general.workspaces', { n: total }, total)"></span>
          </div>
        </template>
      </v-list-item>
      <div class="workspace-list-wrapper">
        <v-list-item
          v-for="(workspace, i) in workspaces"
          class="workspace-item"
          :id="i.toString()"
          density="comfortable"
          variant="flat"
          height="56"
          :key="workspace.workspaceId"
          :active="workspace.workspaceId === currentWorkspace.workspaceId"
          v-intersect="onIntersect"
        >
          <template v-slot:title>
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-2" size="40" icon="$channelWorkspace" />
                <div>
                  <div class="subtitle2 mb-1">{{ workspace.workspaceName }}</div>
                  <div class="body3">{{ workspace.workspaceId }}</div>
                </div>
              </div>
              <v-btn rounded color="primary" @click="config.item.closeCallback(workspace)">
                {{ $t("modals.navigateToGlobalView.actionBtn") }}
              </v-btn>
            </div>
          </template>
        </v-list-item>
        <div v-if="loading" class="d-flex align-center justify-center">
          <v-progress-circular size="40" data-testid="select-hierarchy-progress" indeterminate />
        </div>
      </div>
    </v-list>
  </div>
</template>

<script lang="ts">
import { WorkspaceAction } from "@/constants/workplaces";
import { computed, onMounted, type PropType, ref, watch } from "vue";
import { handleVirtualScrollData, trackGtmEvent } from "@/_helpers/utils.ts";
import { type MspHierarchyItem, useMspStore } from "@/_store/msp/msp.module.ts";
import type { GenericCallback, Pagination } from "@/types.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";

export default {
  props: {
    config: {
      type: Object as PropType<
        DialogDataConfig<{
          closeCallback: GenericCallback;
        }>
      >,
      required: true,
    },
  },
  setup() {
    const mspStore = useMspStore();
    const workspaceSearch = ref("");
    let workspaceTimer: ReturnType<typeof setTimeout>;
    const total = ref(0);
    const workspaces = ref<MspHierarchyItem[]>([]);
    const pagination = ref<Pagination>({
      page: 0,
      pageSize: 10,
    });
    const loading = ref(false);
    const workspaceStore = useWorkspaceStore();
    const currentWorkspace = computed(() => ({
      name: workspaceStore.workspace.name,
      workspaceId: workspaceStore.workspace.workspaceId,
    }));

    const onIntersect = async (isIntersecting: boolean, entries: IntersectionObserverEntry[]) => {
      const intersectedItemId = Number(entries[0].target.id);
      if (isIntersecting && intersectedItemId === (workspaces.value ?? []).length - 1) {
        // last tab reached in backend, don't do new request
        if (intersectedItemId === total.value - 1) {
          return;
        }
        await loadMoreWorkplaces();
      }
    };

    const loadMoreWorkplaces = async () => {
      pagination.value = {
        page: pagination.value.page + 1,
        pageSize: 10,
      };
      await getItems(workspaceSearch.value);
    };

    const getItems = async (search: string = "") => {
      loading.value = true;
      try {
        const { data } = await mspStore.getHierarchies({
          search,
          pagination: pagination.value,
        });
        workspaces.value = handleVirtualScrollData(workspaces.value, data.items, "workspaceId");
        total.value = data.total;
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const updateSearch = (value: string, skipRequest = false) => {
      workspaceSearch.value = value;

      if (skipRequest) return;

      // cancel pending call
      clearTimeout(workspaceTimer);
      // delay new call 500ms
      workspaceTimer = setTimeout(async () => {
        workspaces.value = [];
        pagination.value = {
          page: 0,
          pageSize: 10,
        };
        await getItems(value);
      }, 500);
    };

    watch(workspaceSearch, (newVal) => {
      updateSearch(newVal);
    });

    onMounted(async () => {
      await getItems();
      trackGtmEvent(GtmAction.MSP_WORKSPACE_SELECTOR, GTMParams.MSP_MODAL_OPENED);
    });

    return {
      loading,
      workspaceSearch,
      workplacesActions: WorkspaceAction,
      workspaces,
      total,
      pagination,
      onIntersect,
      currentWorkspace,
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-field__outline {
    display: none !important;
  }
}

.workspace-list-wrapper {
  overflow: auto;
  height: 400px;
}

.workspace-item {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
}
</style>
